import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { SidePanel } from "components/designV2/sidePanel/sidePanel";
import { TextArea } from "components/designV2/textarea/textarea";
import { TimeReportDto } from "model/TimeReport";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  formatDateWithWeekdayLabel,
  formatMinutesDuration,
  formatTimeRangeLabel,
  minutesBetweenTimeInputs,
} from "web-apps/company/utils/timeAndDate";
import { DetailsRow } from "web-apps/company/components/timeReportsV2/components/views/person/components/detailsPanel/detailsRow.component";
import { EditableRangeRow } from "web-apps/company/components/timeReportsV2/components/views/person/components/detailsPanel/editableRangeRow.component";
import { TotalTimeRow } from "web-apps/company/components/timeReportsV2/components/views/person/components/detailsPanel/totalTimeRow.component";
import {
  DataCard,
  Section,
  SectionTitle,
} from "web-apps/company/components/timeReportsV2/components/views/person/components/detailsPanel/timeReportDetailsPanel.styled";
import {
  resolveCanEditTime,
  buildStampSubmitData,
  type ClockStampSubmitData,
  canSubmitStampReport,
  resolveClockStampThresholds,
  resolveDefaultStampTimeInputs,
  type StampReportTimeInputs,
} from "./clockStampTimeReportPanel.logic";

const PANEL_TITLE_ID = "clock-stamp-time-report-panel-title";

interface Props {
  isOpen: boolean;
  onBackdropClick?: () => void;
  onClose: () => void;
  onSubmit: (data: ClockStampSubmitData) => void;
  isSubmitting: boolean;
  timeReport: TimeReportDto;
}

export const ClockStampTimeReportPanel = ({
  isOpen,
  onBackdropClick,
  onClose,
  onSubmit,
  isSubmitting,
  timeReport,
}: Props): JSX.Element => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [companyState] = useCompanyStore();
  const [userNotes, setUserNotes] = useState("");

  const thresholds = resolveClockStampThresholds(companyState.companySettings);
  const canEditTime = resolveCanEditTime(companyState.companySettings);

  const defaultTimeInputs = resolveDefaultStampTimeInputs(
    timeReport,
    thresholds,
  );

  const [reportedStart, setReportedStart] = useState(
    defaultTimeInputs.reportedStart,
  );
  const [reportedEnd, setReportedEnd] = useState(defaultTimeInputs.reportedEnd);
  const [editedBreakStart, setEditedBreakStart] = useState(
    defaultTimeInputs.breakStart,
  );
  const [editedBreakEnd, setEditedBreakEnd] = useState(
    defaultTimeInputs.breakEnd,
  );

  const workerName = `${timeReport.firstName ?? ""} ${
    timeReport.lastName ?? ""
  }`.trim();

  const plannedStart =
    timeReport.companyGigShift?.startTime ?? timeReport.startTime;
  const plannedEnd = timeReport.companyGigShift?.endTime ?? timeReport.endTime;

  const breakMinutes = canEditTime
    ? minutesBetweenTimeInputs(editedBreakStart, editedBreakEnd)
    : Number(timeReport.breakTime) || 0;
  const reportedMinutes = minutesBetweenTimeInputs(reportedStart, reportedEnd);
  const totalMinutes = Math.max(0, reportedMinutes - breakMinutes);

  const breakDisplay =
    breakMinutes > 0
      ? timeReport.breakStartTime && timeReport.breakEndTime
        ? t("TimeReports.BreakWithRange", {
            duration: formatMinutesDuration(breakMinutes),
            range: formatTimeRangeLabel(
              timeReport.breakStartTime,
              timeReport.breakEndTime,
              locale,
            ),
          })
        : formatMinutesDuration(breakMinutes)
      : "0h";

  const stampTimeInputs: StampReportTimeInputs = {
    ...defaultTimeInputs,
    reportedStart,
    reportedEnd,
    breakStart: editedBreakStart,
    breakEnd: editedBreakEnd,
  };

  const isValid = !canEditTime || canSubmitStampReport(stampTimeInputs);

  const handleSubmit = (): void => {
    if (isSubmitting || !isValid) return;

    const data = buildStampSubmitData(
      timeReport,
      canEditTime,
      stampTimeInputs,
      userNotes,
    );
    if (!data) return;

    onSubmit(data);
  };

  const footer = (
    <FooterActions>
      <SecondaryButton
        label={t("WorkerTimeReport.Cancel")}
        size="small"
        onClick={onClose}
      />
      <PrimaryButton
        label={t("WorkerTimeReport.Submit")}
        size="small"
        disabled={isSubmitting || !isValid}
        onClick={handleSubmit}
      />
    </FooterActions>
  );

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      onBackdropClick={onBackdropClick}
      title={t("TimeReports.TimeReport")}
      titleId={PANEL_TITLE_ID}
      footer={footer}
    >
      <AvatarChip title={workerName} subTitle={timeReport.gigRole} />
      <Section>
        <SectionTitle>{t("WorkerTimeReport.ReportTime")}</SectionTitle>
        <DataCard>
          <DetailsRow
            label={t("TimeReports.Date")}
            value={formatDateWithWeekdayLabel(timeReport.startTime, locale)}
          />
          {canEditTime ? (
            <EditableRangeRow
              label={t("WorkerTimeReport.Report")}
              fromLabel={t("General.From")}
              toLabel={t("General.To")}
              startValue={reportedStart}
              endValue={reportedEnd}
              onStartChange={setReportedStart}
              onEndChange={setReportedEnd}
            />
          ) : (
            <DetailsRow
              label={t("WorkerTimeReport.Report")}
              value={`${reportedStart} - ${reportedEnd}`}
            />
          )}
          <DetailsRow
            label={t("TimeReports.Stamped")}
            value={formatTimeRangeLabel(
              timeReport.checkInTime,
              timeReport.checkOutTime,
              locale,
            )}
          />
          <DetailsRow
            label={t("TimeReports.Planned")}
            value={formatTimeRangeLabel(plannedStart, plannedEnd, locale)}
          />
          <TotalTimeRow
            label={t("TimeReports.TotalTime")}
            totalValue={formatMinutesDuration(totalMinutes)}
            overtimeLabel={null}
          />
          {canEditTime ? (
            <EditableRangeRow
              label={t("TimeReports.Break")}
              fromLabel={t("General.From")}
              toLabel={t("General.To")}
              startValue={editedBreakStart}
              endValue={editedBreakEnd}
              onStartChange={setEditedBreakStart}
              onEndChange={setEditedBreakEnd}
            />
          ) : (
            <DetailsRow label={t("TimeReports.Break")} value={breakDisplay} />
          )}
        </DataCard>
      </Section>
      <TextArea
        label={t("WorkerTimeReport.Notes")}
        value={userNotes}
        onChange={setUserNotes}
        placeholder={t("WorkerTimeReport.NotesPlaceHolder")}
        maxLength={500}
        maxHeight={240}
      />
    </SidePanel>
  );
};

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;
